/**
 * RecordCODPaymentDialog — confirmation dialog for recording COD payment.
 *
 * UX Decision 9: Confirmation dialog with pre-filled amount.
 * Shows order total and currency, requires explicit confirmation.
 */

import { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle2, Banknote } from "lucide-react";
import { toast } from "sonner";
import { useRecordCODPayment } from "../api/salesOrderHooks";

interface RecordCODPaymentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  salesOrderName: string;
  grandTotal: number;
  currency: string;
}

export function RecordCODPaymentDialog({
  open,
  onOpenChange,
  salesOrderName,
  grandTotal,
  currency,
}: RecordCODPaymentDialogProps) {
  const [showSuccess, setShowSuccess] = useState(false);
  const recordPaymentMutation = useRecordCODPayment(salesOrderName);
  const autoCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Reset state and clear timeout when dialog opens/closes
  useEffect(() => {
    if (!open) {
      setShowSuccess(false);
      if (autoCloseTimer.current) {
        clearTimeout(autoCloseTimer.current);
        autoCloseTimer.current = null;
      }
    }
  }, [open]);

  const handleConfirm = () => {
    recordPaymentMutation.mutate(undefined, {
      onSuccess: () => {
        setShowSuccess(true);
        // Auto-close after 1.5s per UX spec
        autoCloseTimer.current = setTimeout(() => {
          onOpenChange(false);
        }, 1500);
      },
      onError: (error) => {
        toast.error(
          error.message || "Failed to record payment. Please try again."
        );
      },
    });
  };

  const formattedAmount = new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(grandTotal);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[400px]">
        {showSuccess ? (
          <div className="flex flex-col items-center gap-3 py-6">
            <CheckCircle2 className="h-12 w-12 text-green-500" />
            <p className="text-sm font-medium">Payment recorded!</p>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Banknote className="h-5 w-5" />
                Record COD Payment
              </DialogTitle>
            </DialogHeader>

            <div className="py-2">
              <p className="text-sm text-muted-foreground">
                Record payment of{" "}
                <span className="font-semibold text-foreground">
                  {formattedAmount} {currency}
                </span>{" "}
                received for order{" "}
                <span className="font-medium text-foreground">
                  {salesOrderName}
                </span>
                ?
              </p>
            </div>

            <DialogFooter className="gap-2 sm:gap-0">
              <Button
                variant="ghost"
                onClick={() => onOpenChange(false)}
                disabled={recordPaymentMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirm}
                disabled={recordPaymentMutation.isPending}
              >
                {recordPaymentMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Recording...
                  </>
                ) : (
                  "Confirm"
                )}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
